import React, { useEffect, useMemo, useState } from "react";
import logoAI from "../assets/chatgptWhite.png";
import { useNavigate } from "react-router-dom";
import { FaFile, FaFolder, FaImage, FaUserShield, FaFileCode, FaPowerOff } from "react-icons/fa";
import { FaMessage } from "react-icons/fa6";
import { FaCog } from "react-icons/fa";
import api from "./api/api";
import Gpt from "./Gpt";

const Homepage = () => {
	const navigate = useNavigate();
	const rank = Number(sessionStorage.getItem("rank"));
	const [genList, setGenList] = useState([]);
	const [nowTs, setNowTs] = useState(Date.now());
	const [collapsedMap, setCollapsedMap] = useState({});
	const [showGptModal, setShowGptModal] = useState(false);
	const generationStatusKey = `generationStatus:${sessionStorage.getItem("username") || "anonymous"}`;
	const legacyGenerationStatusKey = "generationStatus";

	useEffect(() => {
		const migrateLegacyStatus = () => {
			const legacyRaw = localStorage.getItem(legacyGenerationStatusKey);
			if (!legacyRaw) return;
			if (localStorage.getItem(generationStatusKey)) {
				localStorage.removeItem(legacyGenerationStatusKey);
				return;
			}
			localStorage.setItem(generationStatusKey, legacyRaw);
			localStorage.removeItem(legacyGenerationStatusKey);
		};

		const syncFromStorage = () => {
			const raw = localStorage.getItem(generationStatusKey);

			if (!raw) {
				if (genList.length > 0) setGenList([]);
				return;
			}
			try {
				const parsed = JSON.parse(raw);
				const list = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
				const filtered = list.filter((item) => item && item.status !== "done");
				setGenList(filtered);
				if (filtered.length > 0) {
					localStorage.setItem(generationStatusKey, JSON.stringify(filtered));
				} else {
					localStorage.removeItem(generationStatusKey);
				}
			} catch (e) {
				if (genList.length > 0) setGenList([]);
			}
		};

		migrateLegacyStatus();
		syncFromStorage();
		const id = setInterval(syncFromStorage, 1500);
		return () => clearInterval(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [generationStatusKey, genList.length]);

	useEffect(() => {
		const id = setInterval(() => setNowTs(Date.now()), 1000);
		return () => clearInterval(id);
	}, []);

	const clearStatus = async (item) => {
		if (item?.projectId && item.status === "running") {
			try {
				await api.delete("/progetti", { data: { ids: [item.projectId] } });
			} catch (e) {
				// ignore backend errors, still clear local state
			}
		}

		const filtered = genList.filter((it) => it.id !== item.id);
		setGenList(filtered);
		setCollapsedMap((prev) => {
			const next = { ...prev };
			delete next[item.id];
			return next;
		});
		if (filtered.length > 0) {
			localStorage.setItem(generationStatusKey, JSON.stringify(filtered));
		} else {
			localStorage.removeItem(generationStatusKey);
		}
	};

	const updateStatusInStorage = (idEntry, patch) => {
		try {
			const raw = localStorage.getItem(generationStatusKey);
			if (!raw) return;
			const parsed = JSON.parse(raw);
			const list = Array.isArray(parsed) ? parsed : [parsed];
			const updated = list.map((item) => (item.id === idEntry ? { ...item, ...patch } : item));
			localStorage.setItem(generationStatusKey, JSON.stringify(updated));
		} catch (e) {
			// ignore
		}
	};

	const toggleCollapsed = (id) => setCollapsedMap((prev) => ({ ...prev, [id]: !prev[id] }));

	const elapsedLabel = (startedAt) => {
		if (!startedAt) return "00:00";
		const totalSeconds = Math.floor((nowTs - Number(startedAt)) / 1000);
		const m = Math.max(0, Math.floor(totalSeconds / 60))
			.toString()
			.padStart(2, "0");
		const s = Math.max(0, totalSeconds % 60)
			.toString()
			.padStart(2, "0");
		return `${m}:${s}`;
	};

	useEffect(() => {
		if (!genList.length) return;
		let active = true;

		const pollStatuses = async () => {
			const running = genList.filter((item) => item.status === "running" && item.jobId);
			if (running.length === 0) return;

			await Promise.all(
				running.map(async (item) => {
					try {
						const res = await api.get(`/generateAI/status/${item.jobId}`);
						if (!active) return;
						const status = res?.data?.status;
						if (status === "done") {
							updateStatusInStorage(item.id, { status: "done", finishedAt: Date.now() });
						} else if (status === "error") {
							updateStatusInStorage(item.id, { status: "error", finishedAt: Date.now(), error: res?.data?.error || null });
						} else if (status === "not_found") {
							updateStatusInStorage(item.id, { status: "error", finishedAt: Date.now(), error: "Job non trovato" });
						}
					} catch (e) {
						if (active && e?.response?.status === 404) {
							updateStatusInStorage(item.id, { status: "error", finishedAt: Date.now(), error: "Generazione interrotta o scaduta. Puoi riprovare dal progetto." });
						}
					}
				})
			);
		};

		const id = setInterval(pollStatuses, 5000);
		pollStatuses();
		return () => {
			active = false;
			clearInterval(id);
		};
	}, [genList, generationStatusKey]);

	const actions = [
		{
			title: "Progetti",
			text: "Crea e gestisci i progetti, verifica i competitor e ricevi il documento finale via email.",
			icon: <FaFolder size={32} className="text-white" />,
			onClick: () => navigate("/Progetti"),
		},
		{
			title: "Chatbot",
			text: "Confrontati con il collega virtuale per idee, prompt e risposte rapide.",
			icon: <FaMessage size={32} className="text-white" />,
			onClick: () => navigate("/Jobs"),
		},
		...(rank === 1
			? [
					{
						title: "Utenze",
						text: "Gestisci utenti, permessi e token di accesso.",
						icon: <FaUserShield size={32} className="text-white" />,
						onClick: () => navigate("/Utenze"),
					},
					{
						title: "Prompts",
						text: "Configura e modifica i prompt utilizzati nei flussi.",
						icon: <FaFileCode size={32} className="text-white" />,
						onClick: () => navigate("/Prompts"),
					},
					{
						title: "Modello GPT",
						text: "Scegli il modello GPT da usare per le chiamate.",
						icon: <FaCog size={32} className="text-white" />,
						onClick: () => setShowGptModal(true),
					},
				]
			: []),
	];

	return (
		<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
			<div
				className="col-span-1 lg:col-span-3 shadow-lg ring-1 ring-black/5 backdrop-blur-sm mt-4 p-4 sm:p-7 flex"
				style={{ background: "linear-gradient(135deg, rgba(35,100,174,0.25), rgba(39,55,62,0.35))", color: "white", borderRadius: "16px" }}
			>
				<div className="relative w-full h-fit">
					<div className="absolute inset-y-0 right-0 top-0 pointer-events-none z-10 hidden xl:block ">
						<img src={logoAI} alt="" className="h-20 w-20 " />
					</div>
					<h1 className="text-xl sm:text-3xl text-white font-bold mb-1">Benvenuta/o, {sessionStorage.getItem("nome")}.</h1>
					<p className="text-white w-full lg:w-5/6 text-justify leading-tight">
						Trasforma le tue idee in documenti straordinari grazie a Miller e l'intelligenza artificiale di OpenAI: l'app perfetta per la creazione rapida e impeccabile
						di documenti.
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 lg:col-span-3">
				{actions.map((action, idx) => (
					<div key={idx} className="box-home group relative cursor-pointer pt-4" onClick={action.onClick} style={{ borderRadius: "14px" }}>
						<div className="absolute right-6 bottom-6 text-white opacity-60 group-hover:opacity-90 transition-opacity pointer-events-none">{action.icon}</div>
						<div className="z-10 relative h-fit flex items-start duration-300">
							<h1 className="font-bold text-2xl mb-1 text-white">{action.title}</h1>
						</div>
						<p className="text-left z-10 relative text-md text-white group-hover:-translate-y-[1%] duration-300 max-w-full leading-snug">{action.text}</p>
					</div>
				))}
				{showGptModal && <Gpt onClose={() => setShowGptModal(false)} />}
				{genList.map((item) => {
					const isCollapsed = collapsedMap[item.id] ?? false;
					return (
						<div
							key={item.id}
							className="col-span-1 md:col-span-2 xl:col-span-3 surface-strong p-4 sm:p-6 flex flex-col gap-3"
						>
							<div className="flex items-start justify-between gap-3">
								<div className="flex-1">
									<p className="text-sm font-medium opacity-80">{item.status === "error" ? "Generazione interrotta" : "Produzione file in corso"}</p>
									<h2 className="text-xl font-bold">{item.status === "error" ? `“${item.project}”` : `Stiamo generando “${item.project}”`}</h2>
									{item.status === "error" ? <p role="alert" className="text-sm">{item.error || "La generazione non è stata completata. Contatta l’assistenza."}</p> : <p className="text-sm opacity-80">Tempo stimato per la generazione del file: 15-20 min</p>}
									{!isCollapsed && item.status !== "error" && (
										<>
											<p className="text-sm opacity-80">
												Avviato alle {new Date(Number(item.startedAt)).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} · Tempo trascorso:{" "}
												{elapsedLabel(item.startedAt)}
											</p>
											{item.status === "done" ? (
												<p className="text-sm opacity-80">File pronto. {item.downloadUrl ? "Scarica il file qui sotto." : "Riceverai il file via email."}</p>
											) : (
												<p className="text-sm opacity-80">Ti avviseremo via email quando il documento sarà pronto.</p>
											)}
										</>
									)}
								</div>
								<div className="flex items-center gap-2">
									<button
										type="button"
										onClick={() => toggleCollapsed(item.id)}
									className="button-prophet px-2.5 py-2 text-xs"
									style={{
										borderRadius: "10px",
										background: "linear-gradient(135deg, rgba(35,100,174,0.75), rgba(39,55,62,0.85))",
									}}
								>
									{isCollapsed ? "Mostra" : "Nascondi"}
								</button>
								<button
									type="button"
									onClick={() => clearStatus(item)}
									className="button-prophet px-2.5 py-2 text-xs"
									style={{
										borderRadius: "10px",
										background: "linear-gradient(135deg, rgba(192,57,43,0.75), rgba(192,57,43,0.9))",
									}}
								>
									{item.status === "error" ? "Chiudi" : "Annulla"}
								</button>
							</div>
						</div>
							{!isCollapsed && item.status !== "error" && (
								<div className="flex items-center gap-3">
									<div className="flex-1 h-3 bg-white/15 rounded-full overflow-hidden">
										<div className="progress-bar-fill" />
									</div>
								{item.status === "done" && (
									<button
										type="button"
										disabled={!item.downloadUrl}
										className="button-prophet px-2.5 py-2 text-xs"
										style={{
											borderRadius: "10px",
											background: "linear-gradient(135deg, rgba(35,100,174,0.75), rgba(39,55,62,0.85))",
											opacity: item.downloadUrl ? 1 : 0.7,
											cursor: item.downloadUrl ? "pointer" : "not-allowed",
										}}
											onClick={() => {
												if (item.downloadUrl) {
													window.open(item.downloadUrl, "_blank");
												}
											}}
										>
											{item.downloadUrl ? "Download" : "Scarica (via email)"}
										</button>
									)}
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default Homepage;
